import { useTranslation } from "react-i18next";
import UserProfilePictureAvatar from "../../avatar/UserProfilePictureAvatar";
import { classNames } from "../../../helpers";
import { useState } from "react";

type StartableRoomCardBaseProps = {
  profilePictureUrl?: string;
  participantName: string;
  sessionStatusText: string;
  telawahName: string;
  actionButtonText: string;
  readingProgress: number;
  sessionNumber: number;
  isEvaluationSession: boolean;
  onEnter: () => void;
};

export default function StartableRoomCard(props: StartableRoomCardBaseProps) {
  const { t } = useTranslation("", { keyPrefix: "page.tutor.dashboard.room" });
  const [isLoading, setIsLoading] = useState(false)
  const {
    profilePictureUrl,
    participantName,
    telawahName,
    sessionStatusText,
    sessionNumber,
    actionButtonText,
    readingProgress,
    isEvaluationSession,
    onEnter,
  } = props;

  const bgColor = "bg-brandblue-600";
  const titleTextColor = "text-white";
  const subtitleTextColor = "text-white";
  const borderColor = isEvaluationSession
    ? "border-4 border-brandblue-800"
    : null;

  return (
    <div
      className={classNames(
        `divide-y sm:divide-y-0 rounded-lg shadow-md p-4 flex gap-4 sm:justify-between flex-col sm:flex-row w-auto sm:w-full mx-auto sm:mx-0`,
        bgColor,
        titleTextColor,
        borderColor
      )}
    >
      {/* start reader info */}
      <div className="flex items-center gap-3 pe-4">
        <UserProfilePictureAvatar
          profilePictureUrl={profilePictureUrl}
          className="w-10 h-10"
          alt="sand-user-img"
        />

        <div className="flex items-start gap-1 flex-col">
          <h3 className={`text-sm font-medium`}>{participantName}</h3>
          <p className={`text-sm font-normal ${subtitleTextColor}`}>
            {telawahName}
          </p>
          <h3 className={`text-sm font-medium md:hidden`}>
            {sessionStatusText}
          </h3>
        </div>
      </div>
      {/* end reader info */}
      {/* start session info */}
      {isEvaluationSession ? (
        <EvaluationSessionInfo sessionNumber={sessionNumber} />
      ) : (
        <RecitationSessionInfo
          sessionNumber={sessionNumber}
          subtitleTextColor={subtitleTextColor}
          readingProgress={readingProgress}
        />
      )}
      {/* end session info */}
      {/* start session time */}
      <div
        className="pe-4 flex justify-center sm:static "
        data-cy="startSessionContainer"
      >
        <button
          type="button"
          onClick={() => {onEnter(); setIsLoading(true)}}
          data-cy="handlerStartSession"
          className="inline-flex h-fit items-center px-4 py-2 mt-2 sm:my-auto border border-transparent text-sm font-medium rounded-md shadow-sm text-brandblue-600 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-50"
          disabled={isLoading}
        >
          {actionButtonText}
        </button>
      </div>
      {/* end session time */}
    </div>
  );
}

type RecitationSessionInfoProps = {
  sessionNumber: number;
  subtitleTextColor: string;
  readingProgress: number;
};

function RecitationSessionInfo(props: RecitationSessionInfoProps) {
  const { t } = useTranslation("", { keyPrefix: "page.tutor.dashboard.room" });
  const { sessionNumber, subtitleTextColor, readingProgress } = props;

  return (
    <div className="pe-4 block">
      <div className="flex items-start gap-1 flex-col">
        <p className={"font-medium text-base"}>
          {t("sessionNum", { num: "# " + sessionNumber })}
        </p>
        <p className={`text-xs font-normal ${subtitleTextColor}`}>
          {`${t("readingProgress", {
            number: `${readingProgress}`,
          })}`}
        </p>
      </div>
    </div>
  );
}

type EvaluationSessionInfoProps = {
  sessionNumber: number;
};

function EvaluationSessionInfo(props: EvaluationSessionInfoProps) {
  const { t } = useTranslation("", { keyPrefix: "page.tutor.dashboard.room" });
  const { sessionNumber } = props;

  return (
    <div className="flex items-center justify-center">
      <p className={"font-medium text-base"}>
        {t("sessionNum", { num: "# " + sessionNumber })}
      </p>
    </div>
  );
}
